import React from 'react';
import { Typography } from '../../@lib/core';
import AlertRoundIcon from '../../@lib/icons/AlertRoundIcon';
import * as styles from './ErrorBoundary.module.css';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

interface WithNewRelic {
  newrelic?: {
    noticeError?: (
      error: Error,
      customAttributes?: Record<string, unknown>,
    ) => void;
  };
}

/**
 * Captura erros de render/lifecycle de qualquer página e exibe um fallback
 * em vez de deixar o React desmontar a árvore inteira — comportamento que
 * resultava em tela branca silenciosa em produção. Reporta o erro ao New
 * Relic quando o agent estiver disponível.
 *
 * Montado por página (key=pathname no gatsby-browser), então navegar para
 * outra rota reseta o boundary automaticamente.
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (typeof window === 'undefined') return;
    const { newrelic } = window as unknown as WithNewRelic;
    newrelic?.noticeError?.(error, {
      componentStack: info?.componentStack ?? '',
    });
  }

  // Sem retry/reload por decisão consciente: no fluxo de autologado o
  // auth_code é de uso único e já foi consumido, então recarregar
  // re-dispararia o login com token morto. O fallback apenas evita a tela
  // branca e orienta o usuário a reabrir pela TIM (token + estado novos).
  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className={styles.container}>
        <AlertRoundIcon size={44} />
        <Typography variant="h5" align="center" color="black">
          Algo deu errado
        </Typography>
        <Typography
          variant="label"
          fontWeight="regular"
          align="center"
          color="var(--color-text-secondary)"
        >
          Não foi possível carregar esta tela. Feche e abra novamente para
          tentar de novo.
        </Typography>
      </div>
    );
  }
}

export default ErrorBoundary;
